import {
  Alert, Box, Button, Grid, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import { Trash01, Plus } from '../../../assets';
import { useContext, useState, useEffect } from 'react';
import { StockCountZonesContext, StockCountZonesContextValues } from '../contexts/StockCountZonesContext';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ItemModel } from '../../../app/services/api/generated';

const StockCountZonesItems = () => {
  const {
    t,
    isLoading,
    handleAddBulkItemClick,
    handleAddItemsClick,
    deleteItemRow,
    itemsData,
    reorderItems,
    categoryData,
  } = useContext(StockCountZonesContext) as StockCountZonesContextValues;

  // Show items in the order they are in itemsData
  const [items, setItems] = useState(itemsData);

  // Sync with context if itemsData changes (e.g. after add/delete/reorder)
  useEffect(() => {
    setItems(itemsData);
  }, [itemsData]);

  const theme = useTheme();
  const hasItems = items.length > 0;

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const newItems = [...items];
    const [removed] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, removed);
    setItems(newItems);
    reorderItems(newItems);
  };

  if (isLoading) return null;

  return (
    <Box sx={{ border: '2px solid', borderRadius: '4px', borderColor: theme.palette.primary[500] }}>
      <Grid container
        sx={{ pt: '12px', pb: '6px', px: '8px' }}>
        <Grid item
          xs={12}
          sm={3}>
          <Typography variant="textMD"
            sx={{ fontWeight: 'bold' }}>{t('items.title')}</Typography>
        </Grid>
        <Grid item
          xs={12}
          sm={9}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
            {hasItems && (
              <Button
                size="lg"
                variant="bad"
                onClick={() => deleteItemRow()}
                startIcon={<Trash01 />}
              >
                {t('items.deleteAll')}
              </Button>
            )}
            <Button size="lg"
              variant="primary"
              onClick={handleAddBulkItemClick}
              startIcon={<Plus />}>
              {t('items.addBulk')}
            </Button>
            <Button size="lg"
              variant="primary"
              onClick={handleAddItemsClick}
              startIcon={<Plus />}>
              {t('items.add')}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ pt: '10px' }}>
        {hasItems ? (
          <TableContainer sx={{ overflowY: 'auto' }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center"
                      width="60px"></TableCell>
                    <TableCell align="center">Row Number</TableCell>
                    <TableCell align="left">{t('items.itemNumber')}</TableCell>
                    <TableCell align="left">{t('items.description')}</TableCell>
                    <TableCell align="right">{t('items.remove')}</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="items-list">
                  {(provided) => (
                    <TableBody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {items.map((item, index) => (
                        <Draggable
                          key={index.toString()}
                          draggableId={index.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                display: 'table-row',
                              }}
                            >
                              <TableCell align="center">
                                <Box
                                  {...provided.dragHandleProps}
                                  sx={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  <DragIndicatorIcon />
                                </Box>
                              </TableCell>
                              <TableCell align="center">{index + 1}</TableCell>
                              <TableCell>{item.itemNumber}</TableCell>
                              <TableCell>{item.itemDescription}</TableCell>
                              <TableCell align="right">
                                <IconButton aria-label='delete'
                                  onClick={() => deleteItemRow(item.itemNumber ?? '')}
                                  size="small"
                                >
                                  <Trash01 style={{ color: 'red' }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </TableContainer>
        ) : (
          <Alert severity="info"
            variant="outlined"
            sx={{ mb: '12px', mx: '8px' }}>
            {categoryData.length > 0 ? t('items.noAdditionalItems') : t('items.noItems')}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default StockCountZonesItems;